'use client'

import { TableBody, TableCell, TableRow } from '@/components/ui/table'
import type { SalaryType } from '@/lib/generated/graphql-types'
import { cn } from '@/lib/css-utils'
import {
  formatEmployerTypeDetails,
  formatResponsibilities,
  formatYearsOfExperience,
  getBenefitsCount,
  getEstimatedFte,
  getTimeAgo,
  getTotalCompensation,
} from '@/modules/salary/common/utils'
import { useSearchParams } from 'next/navigation'
import { Fragment, useMemo, type JSX } from 'react'
import { getJobTypeTaxStatus } from '@/modules/salary/common/salary-details-utils'
import { CALL_SCHEDULE_NONE_VALUE } from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import { PLACEHOLDER_SALARY } from '../constants'
import {
  SALARY_ROW_SELECTOR,
  SalaryRowsProps,
  SalaryVisibility,
} from '@/modules/salary/table/constants'
import { SalaryRowHint } from '@/modules/salary/table/salary-row-hint'
import { Badge } from '@/components/ui/badge'
import { SalaryCompDescription } from '@/modules/salary/common/salary-comp-description'
import { AcademicBadge } from '@/modules/salary/common/accademic-badge'
import {
  CONTRACTING_ENTITY_AGENCY_ID,
  locumsContractingEntityOptions,
} from '@/modules/onboarding/locum-prn-wrap-up/constants'
import { SalaryInteractionIndicator } from './salary-interation-indicator'
import { isShowRowHintRow, useShowRowHint } from './use-show-row-hint'

function PostedCell(salary: SalaryType) {
  return (
    <div className="text-sm text-copy-muted mb-[1.1rem]">
      {getTimeAgo(salary.postedAt)}
    </div>
  )
}

function OccupationCell(salary: SalaryType) {
  const content = [
    `${getJobTypeTaxStatus(salary.jobType, salary.taxStatus)}`,
    getEstimatedFte(salary.estimatedFte),
    formatResponsibilities(salary.responsibilities),
    formatYearsOfExperience(salary.yearsOfExperience),
  ]
  return (
    <>
      <div className="flex gap-2">
        <p className="font-medium text-copy-primary ">
          {salary.occupation?.baseTitlePlusSpecialty}
        </p>
        {salary.submittedByCurrentUser ? (
          <Badge variant="yellow">You</Badge>
        ) : null}
      </div>
      <div className="text-sm text-copy-muted flex items-center gap-1 mt-0.5">
        {content.filter(Boolean).map((item) => (
          <Fragment key={item}>
            {item}
            <span className="text-copy-pale font-bold last:hidden">·</span>
          </Fragment>
        ))}
      </div>
    </>
  )
}

function LocationCell({
  salary,
  isLocumsOrPrn,
}: {
  salary: SalaryType
  isLocumsOrPrn: boolean
}) {
  return (
    <>
      <div className="font-medium text-copy-primary">
        {salary.displayLocation}
      </div>
      <div className="items-center">
        <span className="text-sm text-copy-muted mt-0.5">
          {formatEmployerTypeDetails(
            salary.employerType,
            salary.employerTypeDetails,
            salary.practiceSetting,
            isLocumsOrPrn,
          )}
        </span>
        {salary.academicAffiliation && !isLocumsOrPrn ? (
          <AcademicBadge academicAffiliation={salary.academicAffiliation} />
        ) : null}
      </div>
    </>
  )
}

function PtoBenefitsCell({
  salary,
  isLocumsOrPrn,
}: {
  salary: SalaryType
  isLocumsOrPrn: boolean
}) {
  const benefitsCount = getBenefitsCount(salary)
  const { paidTimeOffWeeks, locums } = salary
  if (isLocumsOrPrn) {
    return (
      <>
        <div className="font-medium text-copy-primary">
          {
            locumsContractingEntityOptions.find(
              (option) => option.id === locums?.contractingEntity,
            )?.label
          }
        </div>
        <div className="text-sm text-copy-muted mt-0.5">
          {locums?.contractingEntity === CONTRACTING_ENTITY_AGENCY_ID
            ? locums?.agencyName
            : null}
        </div>
      </>
    )
  }
  return (
    <>
      <div className="font-medium text-copy-primary">
        {paidTimeOffWeeks === 1
          ? '1 wk'
          : paidTimeOffWeeks
            ? `${paidTimeOffWeeks} wks`
            : ''}
      </div>
      <div className="text-sm text-copy-muted mt-0.5">
        {benefitsCount === 1 ? '1 benefit' : `${benefitsCount} benefits`}
      </div>
    </>
  )
}

function ScheduleCell(salary: SalaryType) {
  return (
    <>
      {salary.avgHoursWorked && (
        <div className="font-medium text-copy-primary">
          {salary.avgHoursWorked} hrs/week
        </div>
      )}
      {salary.callSchedule && (
        <div className="text-sm text-copy-muted mt-0.5">
          {salary.callSchedule.toLowerCase() === CALL_SCHEDULE_NONE_VALUE
            ? 'No Call'
            : `Call ${salary.callSchedule}`}
        </div>
      )}
    </>
  )
}

function TotalCompensationCell({
  salary,
  blurTotalCompensation,
  isHourlyPayPeriod,
}: {
  salary: SalaryType
  blurTotalCompensation: boolean
  isHourlyPayPeriod: boolean
}) {
  const shouldBlurComp =
    blurTotalCompensation ||
    salary.visibility === SalaryVisibility.Restricted ||
    salary.visibility === SalaryVisibility.None
  return (
    <div>
      <div
        className={cn('font-semibold text-green-40 text-right', {
          'font-bold blur-sm': shouldBlurComp,
        })}
      >
        {shouldBlurComp
          ? PLACEHOLDER_SALARY
          : getTotalCompensation(salary, {
              isHourly: isHourlyPayPeriod,
            })}
      </div>
      <div className="text-sm text-copy-muted mt-0.5 text-right">
        <SalaryCompDescription
          salary={salary}
          className={shouldBlurComp ? 'blur-sm' : ''}
        />
      </div>
    </div>
  )
}

function InteractionIndicator(_salary: SalaryType) {
  return (
    <div className="flex items-center justify-end pr-4">
      <SalaryInteractionIndicator show hasCircleBorder={false} />
    </div>
  )
}

export function SalaryTableBody({
  salaries,
  isShowingSalaryDetails,
  blurTotalCompensation,
  onRowClick,
  showTableRowHint,
  isHourlyPayPeriod,
  isLocumsOrPrn,
}: SalaryRowsProps & {
  isHourlyPayPeriod?: boolean
  isLocumsOrPrn?: boolean
}): JSX.Element {
  const searchParams = useSearchParams()

  const { salariesWithRowHint } = useShowRowHint({
    showTableRowHint,
    salaries,
    blurTotalCompensation,
    salaryVisibility: salaries?.[0]?.visibility,
  })

  const columnsConfig = useMemo(
    () => [
      {
        key: 'posted',
        render: PostedCell,
      },
      {
        key: 'occupation',
        render: OccupationCell,
      },
      {
        key: 'location',
        render: (salary: SalaryType) => (
          <LocationCell
            salary={salary}
            isLocumsOrPrn={isLocumsOrPrn ?? false}
          />
        ),
        hidden: isShowingSalaryDetails,
      },
      {
        key: 'pto_benefits',
        render: (salary: SalaryType) => (
          <PtoBenefitsCell
            salary={salary}
            isLocumsOrPrn={isLocumsOrPrn ?? false}
          />
        ),
        hidden: isShowingSalaryDetails || isLocumsOrPrn,
      },
      {
        key: 'schedule',
        render: ScheduleCell,
      },
      {
        key: 'total_comp',
        render: (salary: SalaryType) => (
          <TotalCompensationCell
            salary={salary}
            blurTotalCompensation={blurTotalCompensation ?? false}
            isHourlyPayPeriod={isHourlyPayPeriod ?? false}
          />
        ),
      },
      {
        key: 'interaction_indicator',
        reducedCellPadding: true,
        render: InteractionIndicator,
      },
    ],
    [
      isShowingSalaryDetails,
      isLocumsOrPrn,
      blurTotalCompensation,
      isHourlyPayPeriod,
    ],
  )

  return (
    <TableBody>
      {salariesWithRowHint.map((salary, index) => {
        if (isShowRowHintRow(salary)) {
          return <SalaryRowHint key={index} />
        }

        const isSelected = searchParams.get('salaryId') === salary.salaryId

        return (
          <TableRow
            key={index}
            id={`${SALARY_ROW_SELECTOR}${salary?.salaryId}`}
            isSelected={isSelected}
            data-testid={`desktop-salary-row-${index}`}
            {...(onRowClick
              ? { onClick: () => onRowClick(salary), role: 'button' }
              : {})}
          >
            {columnsConfig
              .filter((column) => !column.hidden)
              .map((column) => {
                return (
                  <TableCell
                    key={column.key}
                    className={column.reducedCellPadding ? 'p-0' : ''}
                  >
                    {column.render(salary)}
                  </TableCell>
                )
              })}
          </TableRow>
        )
      })}
    </TableBody>
  )
}
